import React, { useState } from "react";
import { Button, ButtonGroup, Card, Dropdown } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as MoreSVG } from "go-core/images/svg/actions/more.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ReactComponent as TPayLogo } from "../../../../../../../../../../images/svg/paymentMethods/integrations/tpay-logo.svg";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	PaymentMethodApi,
	PaymentMethodIntegration,
	PaymentMethodSettingApi,
	PaymentMethodTPayPaymentsApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { getPaymentMethodOnlinePayments } from "../../utils";
import { getIntegrationName, isPaymentMethodIntegrationInstalled } from "../../utils/onlinePayments";
import PaymentMethodInstallIntegrationModal from "./Modal/PaymentMethodInstallIntegrationModal";
import PaymentMethodIntegrationModalForm from "./Modal/PaymentMethodIntegrationModalForm";

interface Props {
	integration: PaymentMethodIntegration;
	settings: PaymentMethodSettingApi[];
	form: UseFormReturn<PaymentMethodApi>;
	tPayPayments?: PaymentMethodTPayPaymentsApi;
	setTPayPayments: React.Dispatch<React.SetStateAction<PaymentMethodTPayPaymentsApi | undefined>>;
	defaultSettings: PaymentMethodSettingApi[];
	fetchPaymentMethod?: () => Promise<void>;
}

const PaymentMethodIntegrationsItemForm = ({
	integration,
	settings,
	form,
	tPayPayments,
	setTPayPayments,
	defaultSettings,
	fetchPaymentMethod,
}: Props) => {
	const { t } = useTranslation();
	const [showIntegrationModal, setShowIntegrationModal] = useState(false);
	const [showInstallIntegrationModal, setShowInstallIntegrationModal] = useState(false);
	const isIntegrationInstalled = isPaymentMethodIntegrationInstalled(PaymentMethodIntegration.TPAY, defaultSettings);
	const paymentMethods = getPaymentMethodOnlinePayments();
	const { addFlash } = useFlash();
	const confirmation = useConfirmation();

	const getIcon = () => {
		switch (integration) {
			case PaymentMethodIntegration.TPAY:
				return <TPayLogo />;
			default:
				return null;
		}
	};

	const getConfiguredPaymentMethodsNames = () => {
		if (!isIntegrationInstalled) return null;

		return paymentMethods
			.map((paymentMethod) => {
				const paymentMethodFromSettings = settings.find(
					(setting) => setting.name === paymentMethod && (setting.value === "true" || setting.value === true)
				);

				if (paymentMethodFromSettings)
					return t(`enums.payment_method.online_payment.${paymentMethod.replace("_ENABLED", "")}_LABEL`);
				return "";
			})
			.filter((paymentMethod) => paymentMethod)
			.join(", ");
	};

	const getButtonTitle = () => {
		if (isIntegrationInstalled) {
			return t("lib:common.action.edit");
		}

		return t("lib:common.action.install");
	};

	const handleButtonClick = () => {
		if (isIntegrationInstalled) {
			setShowIntegrationModal(true);
		} else {
			setShowInstallIntegrationModal(true);
		}
	};

	const handleRemoveIntegration = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("modules.payment_method.field.remove_integration_confirmation_message.title"),
			});
			await apiOrganization.removeTPayPaymentsIntegration();
			if (fetchPaymentMethod) {
				await fetchPaymentMethod();
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<>
			<Card>
				<Card.Body>
					<div className="d-flex justify-content-between align-items-center">
						<div className="d-flex gap-3">
							{getIcon()}
							<div className="d-flex flex-column gap-1 justify-content-center">
								<p className="font-weight-bold mb-0">{getIntegrationName(integration, t)}</p>
								{getConfiguredPaymentMethodsNames() && (
									<small className="text-muted">{getConfiguredPaymentMethodsNames()}</small>
								)}
							</div>
						</div>
						<div className="d-flex align-items-center gap-2 payment-method-integration-item">
							<Button
								variant={isIntegrationInstalled ? "outline-secondary" : "primary"}
								onClick={handleButtonClick}
							>
								{getButtonTitle()}
							</Button>
							{isIntegrationInstalled && (
								<Dropdown as={ButtonGroup}>
									<Dropdown.Toggle as={MoreSVG} />
									<Dropdown.Menu>
										<Dropdown.Item onClick={handleRemoveIntegration}>
											{t("lib:common.action.remove")}
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							)}
						</div>
					</div>
				</Card.Body>
			</Card>
			{showIntegrationModal && (
				<PaymentMethodIntegrationModalForm
					show={showIntegrationModal}
					onHide={() => setShowIntegrationModal(false)}
					settings={settings}
					integration={integration}
					form={form}
					paymentsTypes={tPayPayments?.types}
				/>
			)}
			{showInstallIntegrationModal && (
				<PaymentMethodInstallIntegrationModal
					integration={integration}
					show={showInstallIntegrationModal}
					setShowIntegrationModal={setShowIntegrationModal}
					onHide={() => setShowInstallIntegrationModal(false)}
					setTPayPayments={setTPayPayments}
					fetchPaymentMethod={fetchPaymentMethod}
				/>
			)}
		</>
	);
};

export default PaymentMethodIntegrationsItemForm;

// t("enums.payment_method.online_payment.BLIK_OFFLINE_LABEL");
// t("enums.payment_method.online_payment.BLIK_ONLINE_LABEL");
// t("enums.payment_method.online_payment.APPLE_PAY_LABEL");
// t("enums.payment_method.online_payment.GOOGLE_PAY_LABEL");
// t("enums.payment_method.online_payment.PAYMENT_CARD_LABEL");
// t("enums.payment_method.online_payment.E_TRANSFER_LABEL");
// t("enums.payment_method.online_payment.BLIK_OFFLINE_HELP_TEXT");
// t("enums.payment_method.online_payment.BLIK_ONLINE_HELP_TEXT");
// t("enums.payment_method.online_payment.APPLE_PAY_HELP_TEXT");
// t("enums.payment_method.online_payment.GOOGLE_PAY_HELP_TEXT");
// t("enums.payment_method.online_payment.PAYMENT_CARD_HELP_TEXT");
// t("enums.payment_method.online_payment.E_TRANSFER_HELP_TEXT");
